import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import useUserContext from 'Context/UserContext';
import DataContainer from 'Components/UI/DataContainer';
import useMobileLayout from 'Hooks/useMobileLayout';
import FinancialWellnessComponentCard
  from 'Components/ClientApp/CombinedApp/Viewport/Dashboard/FinancialWellness/FinancialWellnessComponentCard';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';

const styles = theme => ({
  root: {},
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  chip: {
    marginRight: theme.spacing(1),
    color: 'white',
    fontWeight: 600,
    padding: `0 ${theme.spacing(1)}px`,
    backgroundColor: theme.palette.facet.primaryGray,
    '&:hover': {
      backgroundColor: theme.palette.facet.darkGray,
    },
    '&:focus': {
      backgroundColor: theme.palette.facet.accentBlue3,
    },
  },
  activeChip: {
    backgroundColor: theme.palette.facet.accentBlue3,
    '&:hover': {
      backgroundColor: theme.palette.facet.accentBlue4,
    },
  },
  disabledChip: {
    cursor: 'not-allowed',
  },
  component: {
    flexBasis: '240px',
    margin: theme.spacing(1),
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  componentsMobile: {
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
  },
  mobileComponent: {
    margin: theme.spacing(2, 0),
  },
});

const availableDeltas = { '1M': date => date.subtract(1, 'month'), '3M': date => date.subtract(3, 'month'), '1Y': date => date.subtract(1, 'year') };

const WellnessView = ({
  classes,
  className,
  financialWellnessCollection,
}) => {
  const { isAgent } = useUserContext();
  const isMobile = useMobileLayout();
  const financialWellnessResource = useMemo(() => financialWellnessCollection?.resources().head(), [financialWellnessCollection]);
  const loading = financialWellnessResource === null;
  const [activeChip, setActiveChip] = useState(null);
  const [deltas, setDeltas] = useState({});
  const wellnessItems = deltas[activeChip] || deltas['1M'] || [];

  const handleChipClick = label => () => {
    setActiveChip(label);
  };

  const isChipDisabled = useCallback((key) => {
    if (!deltas[key]?.length) return true;
    try {
      for (const delta of deltas[key]) {
        if (delta.attr('values').resources().head().attr('delta')) return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  }, [deltas]);

  useEffect(() => {
    async function getDeltas() {
      const promises = [];
      const deltaMap = {};
      for (const delta in availableDeltas) { // eslint-disable-line guard-for-in,no-restricted-syntax
        const now = moment(new Date());
        const adjusted = availableDeltas[delta](now).valueOf();
        const promise = financialWellnessResource.link('self').Get({ params: { delta: adjusted } });
        deltaMap[delta] = promise.then(data => data);
        promises.push(promise);
      }
      await Promise.all(promises);
      for (const key in deltaMap) { // eslint-disable-line guard-for-in,no-restricted-syntax
        // eslint-disable-next-line no-await-in-loop
        const res = await deltaMap[key]; // eslint-disable-line no-await-in-loop -- get data out of promise, this shouldn't take any time since we already await-ed
        deltaMap[key] = res.resources().head().attr('components').resources()
          .list();
      }
      setDeltas(deltaMap);
    }

    if (!financialWellnessResource) return;
    getDeltas();
  }, [financialWellnessResource]);

  useEffect(() => {
    if (activeChip) return;

    for (const delta in availableDeltas) { // eslint-disable-line guard-for-in,no-restricted-syntax
      if (!isChipDisabled(delta)) {
        setActiveChip(delta);
        return;
      }
    }
  }, [isChipDisabled]);


  return (
    <div className={classNames(classes.root, className)}>
      <DataContainer loading={loading}>
        <span>
          {Object.keys(availableDeltas).map(key => (
            <span className={classNames({ [classes.disabledChip]: activeChip !== key })}>
              <Chip label={key} className={classNames(classes.chip, { [classes.activeChip]: activeChip === key })} size="small" onClick={handleChipClick(key)} disabled={isChipDisabled(key)} />
            </span>
          ))}
        </span>
        <div className={classes.cards}>
          {wellnessItems.map((resource) => {
            return (
              <FinancialWellnessComponentCard
                className={classNames(classes.component, {
                  [classes.mobileComponent]: isMobile,
                })}
                key={resource.id()}
                financialWellnessCollection={financialWellnessCollection}
                resource={resource}
                readOnly={isAgent}
                showDelta
              />
            );
          })}
        </div>
      </DataContainer>
    </div>
  );
};

WellnessView.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  financialWellnessCollection: PropTypes.object.isRequired,
};

WellnessView.defaultProps = {
  className: '',
};

export default withStyles(styles)(WellnessView);
